import React, { useState, useEffect } from 'react';
import { makeApiRequest, API_ENDPOINTS } from '../config/api';
import './About.css';

interface ContentSection {
  type: string;
  content: string;
}

interface AboutContent {
  hero?: {
    title: string;
    subtitle: string;
  };
  main_content?: {
    title: string;
    sections: ContentSection[];
  };
  video?: {
    src: string;
    caption?: string;
  };
  image?: {
    src: string;
    alt: string;
    caption?: string;
  };
  sections?: ContentSection[];
  values?: Array<{
    title: string;
    description: string;
    icon: string;
  }>;
  story?: {
    title: string;
    content: string;
  };
  mission?: {
    title: string;
    content: string;
  };
  vision?: {
    title: string;
    content: string;
  };
}

interface PageData {
  page: {
  id: string;
  title: string;
  content: AboutContent;
  meta_description: string;
    updated_at: string;
  };
}

const About: React.FC = () => {
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await makeApiRequest(API_ENDPOINTS.PAGES_BY_ID('about'));
        
        if (response.ok) {
          const data = await response.json();
          setPageData(data);
        } else {
          setError('Failed to load About page data');
        }
      } catch (err) {
        console.error('Error fetching About page data:', err);
        setError('Failed to load About page data');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) {
    return (
      <div className="about-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading About page...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="about-page">
        <div className="error-container">
          <h2>Error Loading Page</h2>
          <p>{error || 'Failed to load About page data'}</p>
        </div>
      </div>
    );
  }

  const { content } = pageData.page;

  return (
      <div className="about-page">
      {/* Hero Section */}
        <section className="page-header">
          <div className="container">
          <h1>{content.hero?.title || pageData.page.title || 'About'}</h1>
          <p className="page-subtitle">
            {content.hero?.subtitle || 'Driving Innovation Through Intelligent Technology'}
          </p>
          </div>
        </section>

      {/* Main Content - Side by Side Layout (Video Left, Story Right) */}
      {(content.video || content.image || content.main_content) && (
        <section className="about-content">
          <div className="container">
            <div className="about-grid">
              {/* Video or Image on Left */}
              <div className="about-media">
                {content.video ? (
                  <div className="video-container">
                    <video
                      className="about-video"
                      autoPlay
                      muted
                      loop
                      playsInline
                      onError={(e) => {
                        console.log('Video failed to load');
                        (e.target as HTMLVideoElement).style.display = 'none';
                      }}
                    >
                      <source src={content.video.src} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : content.image ? (
                  <div className="image-container">
                    <img
                      src={content.image.src}
                      alt={content.image.alt}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="image-fallback">
                      <div className="fallback-icon">🏙️</div>
                      <p>Smart City Technology</p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Story Content on Right */}
              <div className="about-text">
                {content.story && (
                  <>
                    <h2>{content.story.title}</h2>
                    <div className="content-text" dangerouslySetInnerHTML={{ __html: content.story.content }} />
                  </>
                )}
                {content.main_content && !content.story && (
                  <>
                    <h2>{content.main_content.title}</h2>
                    {content.main_content.sections && content.main_content.sections.map((section, index) => (
                      <div key={index} className="content-section">
                        {section.type === 'paragraph' && (
                          <p>{section.content}</p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Mission & Vision */}
      {(content.mission || content.vision) && (
        <section className="mission-vision">
          <div className="container">
            <div className="row">
              {content.mission && (
                <div className="col-md-6">
                  <div className="mission-card">
                    <h2>{content.mission.title}</h2>
                    <div className="content-text" dangerouslySetInnerHTML={{ __html: content.mission.content }} />
                  </div>
                    </div>
              )}
              {content.vision && (
                <div className="col-md-6">
                  <div className="vision-card">
                    <h2>{content.vision.title}</h2>
                    <div className="content-text" dangerouslySetInnerHTML={{ __html: content.vision.content }} />
                  </div>
                </div>
              )}
              </div>
            </div>
          </section>
        )}

      {/* Values Section */}
      {content.values && content.values.length > 0 && (
        <section className="values-section">
            <div className="container">
            <h2>Our Values</h2>
            <div className="values-grid">
              {content.values.map((value, index) => (
                <div key={index} className="value-card">
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        
      {/* Additional Content Sections */}
      {content.sections && content.sections.length > 0 && (
        <section className="content-sections">
          <div className="container">
            {content.sections.map((section, index) => (
              <div key={index} className="content-section">
                {section.type === 'text' && (
                  <div className="text-content" dangerouslySetInnerHTML={{ __html: section.content }} />
                )}
                {section.type === 'paragraph' && (
                  <p className="content-text">{section.content}</p>
                )}
                {section.type === 'image' && (
                  <div className="image-content">
                    <img src={section.content} alt="About us" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      </div>
  );
};

export default About; 